"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useArtistPreviewModal, type ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
import { useWaveCreatorPreviewModal } from "@/hooks/useWaveCreatorPreviewModal";
import { closeAllCustomTooltips } from "@/helpers/tooltip.helpers";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { WaveCreatorBadge } from "./WaveCreatorBadge";
import { WaveCreatorPreviewModal } from "./WaveCreatorPreviewModal";
interface DropAuthorBadgesProfile {
  readonly id?: string | null;
  readonly handle?: string | null;
  readonly pfp?: string | null;
  readonly banner1_color?: string | null;
  readonly banner2_color?: string | null;
  readonly cic?: number;
  readonly rep?: number;
  readonly tdh?: number;
  readonly tdh_rate?: number;
  readonly xtdh?: number;
  readonly xtdh_rate?: number;
  readonly level?: number;
  readonly primary_address?: string | null;
  readonly primary_wallet?: string | null;
  readonly archived?: boolean;
  readonly active_main_stage_submission_ids?: readonly string[] | null;
  readonly winner_main_stage_drop_ids?: readonly string[] | null;
  readonly artist_of_prevote_cards?: readonly number[] | null;
  readonly profile_wave_id?: string | null;
  readonly is_wave_creator?: boolean | null;
}
interface DropAuthorBadgesProps {
  readonly profile: DropAuthorBadgesProfile;
  readonly tooltipIdPrefix?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly onArtistPreviewOpen?: ((params: {
    readonly user: ApiProfileMin;
    readonly initialTab: ArtistPreviewTab;
  }) => void) | undefined;
  readonly onWaveCreatorPreviewOpen?: ((user: ApiProfileMin) => void) | undefined;
}
const DEFAULT_CONTAINER_CLASS = "tw-inline-flex tw-items-center tw-gap-x-1.5";
const toApiProfileMin = (profile: DropAuthorBadgesProfile): ApiProfileMin => {
  const primaryAddress = profile.primary_address ?? profile.primary_wallet ?? "";
  const fallbackId = primaryAddress;
  return {
    id: profile.id ?? fallbackId,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: profile.banner1_color ?? null,
    banner2_color: profile.banner2_color ?? null,
    cic: profile.cic ?? 0,
    rep: profile.rep ?? 0,
    tdh: profile.tdh ?? 0,
    tdh_rate: profile.tdh_rate ?? 0,
    xtdh: profile.xtdh ?? 0,
    xtdh_rate: profile.xtdh_rate ?? 0,
    level: profile.level ?? 0,
    primary_address: primaryAddress,
    subscribed_actions: [],
    archived: profile.archived ?? false,
    active_main_stage_submission_ids: profile.active_main_stage_submission_ids !== null && profile.active_main_stage_submission_ids !== undefined ? [...profile.active_main_stage_submission_ids] : [],
    winner_main_stage_drop_ids: profile.winner_main_stage_drop_ids !== null && profile.winner_main_stage_drop_ids !== undefined ? [...profile.winner_main_stage_drop_ids] : [],
    artist_of_prevote_cards: profile.artist_of_prevote_cards !== null && profile.artist_of_prevote_cards !== undefined ? [...profile.artist_of_prevote_cards] : [],
    profile_wave_id: profile.profile_wave_id ?? null,
    is_wave_creator: profile.is_wave_creator === true
  };
};
export const DropAuthorBadges: React.FC<DropAuthorBadgesProps> = t0 => {
  const $ = _c(44);
  const {
    profile,
    tooltipIdPrefix: t1,
    className: t2,
    size: t3,
    onArtistPreviewOpen,
    onWaveCreatorPreviewOpen
  } = t0;
  const tooltipIdPrefix = t1 === undefined ? "author-badges" : t1;
  const className = t2 === undefined ? DEFAULT_CONTAINER_CLASS : t2;
  const size = t3 === undefined ? "default" : t3;
  const submissionCount = getSubmissionCount(profile);
  const trophyCount = getTrophyArtworkCount(profile);
  const hasActivityBadge = submissionCount > 0 || trophyCount > 0;
  const isWaveCreator = profile.is_wave_creator === true;
  let t4;
  if ($[0] !== profile) {
    t4 = toApiProfileMin(profile);
    $[0] = profile;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const modalUser = t4;
  const {
    isModalOpen: isArtistPreviewOpen,
    activeTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleTabChange: handleArtistTabChange,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  const {
    isModalOpen: isWaveCreatorPreviewOpen,
    handleBadgeClick: handleWaveCreatorBadgeClick,
    handleModalClose: handleWaveCreatorModalClose
  } = useWaveCreatorPreviewModal();
  let t5;
  if ($[2] !== handleArtistBadgeClick || $[3] !== modalUser || $[4] !== onArtistPreviewOpen) {
    t5 = tab => {
      closeAllCustomTooltips();
      if (onArtistPreviewOpen) {
        onArtistPreviewOpen({
          user: modalUser,
          initialTab: tab
        });
        return;
      }
      handleArtistBadgeClick(tab);
    };
    $[2] = handleArtistBadgeClick;
    $[3] = modalUser;
    $[4] = onArtistPreviewOpen;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const onArtistBadgeClick = t5;
  let t6;
  if ($[6] !== handleWaveCreatorBadgeClick || $[7] !== modalUser || $[8] !== onWaveCreatorPreviewOpen) {
    t6 = () => {
      closeAllCustomTooltips();
      if (onWaveCreatorPreviewOpen) {
        onWaveCreatorPreviewOpen(modalUser);
        return;
      }
      handleWaveCreatorBadgeClick();
    };
    $[6] = handleWaveCreatorBadgeClick;
    $[7] = modalUser;
    $[8] = onWaveCreatorPreviewOpen;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const onWaveCreatorBadgeClick = t6;
  if (!hasActivityBadge && !isWaveCreator) {
    return null;
  }
  let t7;
  if ($[10] !== hasActivityBadge || $[11] !== onArtistBadgeClick || $[12] !== size || $[13] !== submissionCount || $[14] !== tooltipIdPrefix || $[15] !== trophyCount) {
    t7 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} trophyCount={trophyCount} onBadgeClick={onArtistBadgeClick} tooltipId={`${tooltipIdPrefix}-activity`} size={size} />;
    $[10] = hasActivityBadge;
    $[11] = onArtistBadgeClick;
    $[12] = size;
    $[13] = submissionCount;
    $[14] = tooltipIdPrefix;
    $[15] = trophyCount;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== isWaveCreator || $[18] !== onWaveCreatorBadgeClick || $[19] !== size || $[20] !== tooltipIdPrefix) {
    t8 = isWaveCreator && <WaveCreatorBadge tooltipId={`${tooltipIdPrefix}-wave-creator`} onBadgeClick={onWaveCreatorBadgeClick} size={size} />;
    $[17] = isWaveCreator;
    $[18] = onWaveCreatorBadgeClick;
    $[19] = size;
    $[20] = tooltipIdPrefix;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== className || $[23] !== t7 || $[24] !== t8) {
    t9 = <div className={className}>{t7}{t8}</div>;
    $[22] = className;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== activeTab || $[27] !== handleArtistModalClose || $[28] !== handleArtistTabChange || $[29] !== hasActivityBadge || $[30] !== isArtistPreviewOpen || $[31] !== modalUser || $[32] !== onArtistPreviewOpen) {
    t10 = hasActivityBadge && !onArtistPreviewOpen && <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={modalUser} activeTab={activeTab} onTabChange={handleArtistTabChange} />;
    $[26] = activeTab;
    $[27] = handleArtistModalClose;
    $[28] = handleArtistTabChange;
    $[29] = hasActivityBadge;
    $[30] = isArtistPreviewOpen;
    $[31] = modalUser;
    $[32] = onArtistPreviewOpen;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== handleWaveCreatorModalClose || $[35] !== isWaveCreator || $[36] !== isWaveCreatorPreviewOpen || $[37] !== modalUser || $[38] !== onWaveCreatorPreviewOpen) {
    t11 = isWaveCreator && !onWaveCreatorPreviewOpen && <WaveCreatorPreviewModal isOpen={isWaveCreatorPreviewOpen} onClose={handleWaveCreatorModalClose} user={modalUser} />;
    $[34] = handleWaveCreatorModalClose;
    $[35] = isWaveCreator;
    $[36] = isWaveCreatorPreviewOpen;
    $[37] = modalUser;
    $[38] = onWaveCreatorPreviewOpen;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t9;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  return t12;
};